import React from 'react'

import { useLocalSoundCache } from '../../providers/LocalSoundCacheProvider'

const useAudioClip = (src: string) => {
  const { loadSound, status, sounds } = useLocalSoundCache()
  const [clip, setClip] = React.useState<HTMLAudioElement>(null)
  const [canPlay, setCanPlay] = React.useState(false)
  const [queued, setQueued] = React.useState(false)

  // Load sound
  React.useEffect(() => {
    if (status[src] === 'DONE') {
      setClip(sounds[src])
      setCanPlay(true)
      return
    }

    if (status[src] !== 'FETCHING') {
      loadSound(src)
    }
  }, [loadSound, status, sounds, src])

  // Plays the clip and handled the error that can arise
  const playSound = React.useCallback(async (clip: HTMLAudioElement) => {
    try {
      await clip.play()
      return true
    } catch {}

    return false
  }, [])

  // Plays the sound if it was queued
  React.useEffect(() => {
    if (!canPlay || !queued) {
      return
    }

    let mounted = true

    const handlePlay = async () => {
      const successful = await playSound(clip)

      if (mounted) {
        setQueued(!successful)
      }
    }

    handlePlay()

    return () => {
      mounted = false
    }
  }, [canPlay, queued, clip, playSound])

  // Plays the clip
  const play = React.useCallback(() => {
    if (canPlay) {
      playSound(clip)
      return
    }

    // Queue the sound for playing if it hasn't loaded yet
    setQueued(true)
  }, [canPlay, playSound, clip])

  return play
}

export default useAudioClip
